import { useEffect, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, Info, Phone, User, Gauge, Fuel, Check } from "lucide-react";

/* -----------------------------------------------------------------
 * Small building blocks reused by every demo panel.  Everything here
 * is stateless / display-only so the DemoStage can freely swap them.
 * -----------------------------------------------------------------*/

export function DemoBadge({ children = "DEMO" }) {
  return (
    <span data-testid="demo-badge"
          className="inline-flex items-center gap-1 rounded-full bg-rose-600 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5">
      {children}
    </span>
  );
}

export function KV({ rows }) {
  return (
    <dl className="text-sm grid grid-cols-3 gap-x-3 gap-y-1.5">
      {rows.filter(([, v]) => v !== undefined && v !== null && v !== "").map(([k, v]) => (
        <div key={k} className="col-span-3 grid grid-cols-3 gap-x-3">
          <dt className="col-span-1 text-slate-500 text-xs uppercase tracking-wider">{k}</dt>
          <dd className="col-span-2 text-slate-900">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

/* -----------------------------------------------------------------
 * Progressive channel chat (Messenger / WhatsApp / SMS / phone / walk-in)
 * -----------------------------------------------------------------*/

const THEME = {
  messenger: { name: "Simulated Messenger conversation", bubble: "bg-sky-100 text-sky-900",   bg: "bg-slate-50",  accent: "text-sky-700" },
  whatsapp:  { name: "Simulated WhatsApp conversation",  bubble: "bg-emerald-100 text-emerald-900", bg: "bg-emerald-50/40", accent: "text-emerald-700" },
  sms:       { name: "Simulated SMS thread",              bubble: "bg-slate-200 text-slate-900", bg: "bg-white",     accent: "text-slate-700" },
  phone:     { name: "Simulated phone call",              bubble: "bg-indigo-100 text-indigo-900", bg: "bg-indigo-50/40", accent: "text-indigo-700" },
  walkin:    { name: "Simulated walk-in transcript",      bubble: "bg-amber-100 text-amber-900", bg: "bg-amber-50/40",   accent: "text-amber-700" },
};

// Compresses scripted message timestamps so the conversation plays
// noticeably faster while keeping messages' relative pacing to each other.
const CHAT_SPEED_FACTOR = 0.4;

export function ChannelChat({ channel, messages, live = true, runId = 0, fast = false }) {
  const theme = THEME[channel?.kind] || THEME.messenger;
  const [shown, setShown] = useState(live ? 0 : messages.length);
  const timers = useRef([]);
  const scrollRegionRef = useRef(null);
  // `fast` is scoped to the homepage embed only: the promoter/company
  // dashboard demo tool and the standalone public catalog keep the
  // original pacing (and normal full-page scroll instead of a bounded,
  // auto-following chat window).
  const speedFactor = fast ? CHAT_SPEED_FACTOR : 1;

  useEffect(() => {
    if (!live) return;
    setShown(0);
    timers.current.forEach(clearTimeout);
    timers.current = messages.map((m, i) =>
      setTimeout(() => setShown(v => Math.max(v, i + 1)), (m.at || 400 * (i + 1)) * speedFactor)
    );
    return () => timers.current.forEach(clearTimeout);
  }, [messages, live, runId, speedFactor]);

  useEffect(() => {
    if (!fast) return;
    const region = scrollRegionRef.current;
    if (region) region.scrollTop = region.scrollHeight;
  }, [fast, shown]);

  const IconComponent = channel?.kind === "phone" ? Phone : User;

  return (
    <div data-testid="channel-chat" className={`rounded-xl border border-slate-200 ${theme.bg} overflow-hidden`}>
      <div className="flex items-center justify-between px-3 py-2 bg-white/80 border-b border-slate-200">
        <div className="flex items-center gap-2 text-xs text-slate-600">
          <IconComponent className="w-3.5 h-3.5" />
          <span className={`font-semibold ${theme.accent}`}>{theme.name}</span>
        </div>
        <DemoBadge>DEMO</DemoBadge>
      </div>
      <div ref={scrollRegionRef} className={`px-3 py-3 space-y-2 min-h-[220px] ${fast ? "max-h-[380px] overflow-y-auto overscroll-contain" : ""}`}>
        {messages.slice(0, shown).map((m, i) => (
          <ChatBubble key={i} m={m} theme={theme} />
        ))}
        {shown < messages.length && (
          <div className="text-[11px] text-slate-400 pl-1">typing…</div>
        )}
        <div />
      </div>
      <div className="px-3 py-2 border-t border-slate-200 text-[11px] text-slate-500">
        {channel?.business} · {channel?.customer}
      </div>
    </div>
  );
}

function ChatBubble({ m, theme }) {
  if (m.from === "system") {
    return (
      <div className="text-center text-[11px] text-slate-500 italic px-2 py-1">
        {m.text}
      </div>
    );
  }
  const alignRight = m.from === "staff";
  return (
    <div className={`flex ${alignRight ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm leading-relaxed ${alignRight ? "bg-slate-800 text-white" : theme.bubble}`}>
        <div className="text-[10px] uppercase tracking-wider opacity-70 mb-0.5">{alignRight ? "Staff" : "Customer"}</div>
        {m.text}
      </div>
    </div>
  );
}

/* -----------------------------------------------------------------
 * Panels: each renders inside the "ConfirmPH surface" column
 * -----------------------------------------------------------------*/

function PanelShell({ title, testId, children, tone = "default" }) {
  const toneStyles = {
    default: "border-slate-200 bg-white",
    warn:    "border-amber-300 bg-amber-50",
    ok:      "border-emerald-300 bg-emerald-50",
  }[tone];
  return (
    <div data-testid={testId} className={`rounded-xl border ${toneStyles} p-4 shadow-sm`}>
      <div className="flex items-center justify-between mb-2 gap-2">
        <div className="font-semibold text-slate-900 text-sm">{title}</div>
        <DemoBadge>DEMO</DemoBadge>
      </div>
      {children}
    </div>
  );
}

export function NotepadPanel({ title, data }) {
  return (
    <PanelShell testId="panel-notepad" title={title || "ConfirmPH Notepad"}>
      <KV rows={[
        ["Customer",  data?.customer],
        ["Contact",   data?.contact],
        ["Vehicle",   data?.vehicle],
        ["Pickup",    data?.pickup],
        ["Location",  data?.pickup_location],
        ["Return",    data?.ret],
        ["Price",     data?.price],
        ["Notes",     data?.notes],
      ]} />
    </PanelShell>
  );
}

export function DraftPanel({ title, data }) {
  return (
    <PanelShell testId="panel-draft" title={title || "Booking draft"}>
      <KV rows={[
        ["Customer",  data?.customer],
        ["Vehicle",   data?.vehicle],
        ["Pickup",    data?.pickup],
        ["Return",    data?.ret],
        ["Price",     data?.price],
        ["Subtotal",  data?.subtotal],
        ["Deposit",   data?.deposit],
        ["Balance",   data?.balance],
        ["Source",    data?.source],
        ["Status",    data?.status],
      ]} />
    </PanelShell>
  );
}

export function BookingPanel({ title, data }) {
  return (
    <PanelShell testId="panel-booking" title={title || "Booking"}>
      <KV rows={[
        ["Customer",  data?.customer],
        ["Vehicle",   data?.vehicle],
        ["Pickup",    data?.pickup],
        ["Return",    data?.ret],
        ["Subtotal",  data?.subtotal],
        ["Deposit",   data?.deposit],
        ["Balance",   data?.balance],
        ["Source",    data?.source],
        ["Status",    data?.status],
      ]} />
    </PanelShell>
  );
}

export function ConfirmationSentPanel({ title }) {
  return (
    <PanelShell testId="panel-confirmation-sent" title={title || "Confirmation link generated"} tone="ok">
      <div className="text-sm text-slate-700">Send it over Messenger, SMS, or copy the link.</div>
      <div className="mt-3 flex items-center gap-2">
        <div className="flex-1 rounded-lg bg-white border border-slate-300 px-3 py-2 text-xs font-mono text-slate-700 truncate">
          confirmph.app/c/DEMO-8f2a…
        </div>
        <button disabled className="px-3 py-2 rounded-lg bg-emerald-700 text-white text-xs">Copy</button>
      </div>
    </PanelShell>
  );
}

export function CustomerConfirmationPanel({ title, onConfirm }) {
  return (
    <PanelShell testId="panel-customer-confirmation" title={title || "Customer confirmation page"}>
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
        <div className="font-semibold text-slate-900">Confirm your booking</div>
        <div className="text-xs text-slate-600 mt-1">This is what your customer sees on their phone.</div>
      </div>
      <div className="mt-3 flex justify-end">
        <button data-testid="panel-customer-confirm-btn" onClick={onConfirm}
                className="px-3 py-2 rounded-full bg-emerald-700 text-white text-xs font-medium">
          Simulate customer taps Confirm
        </button>
      </div>
    </PanelShell>
  );
}

export function ConfirmedPanel({ title, ok }) {
  return (
    <PanelShell testId="panel-confirmed" title={title || "Customer Confirmed"} tone="ok">
      <div className="flex items-center gap-2 text-emerald-800 font-semibold text-sm">
        <CheckCircle2 className="w-4 h-4" /> {ok || "Confirmed"}
      </div>
    </PanelShell>
  );
}

export function VehiclePanel({ title, data }) {
  const statusTone = /available/i.test(data?.status || "")
    ? "text-emerald-700 bg-emerald-50 border-emerald-200"
    : "text-amber-800 bg-amber-50 border-amber-200";
  return (
    <PanelShell testId="panel-vehicle" title={title || "Vehicle"}>
      <div className="text-sm">
        <div className="font-semibold text-slate-900">{data?.model}</div>
        <div className="text-xs text-slate-600 font-mono">{data?.plate}</div>
        <div className={`mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] ${statusTone}`}>
          {data?.status}
        </div>
        {data?.upcoming && (
          <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 p-2 text-xs text-slate-700">
            <span className="uppercase tracking-wider text-slate-500 font-semibold">Upcoming</span>
            <div className="mt-0.5">{data.upcoming}</div>
          </div>
        )}
      </div>
    </PanelShell>
  );
}

export function ConflictPanel({ title, warn, data }) {
  return (
    <PanelShell testId="panel-conflict" title={title || "Booking Conflict"} tone="warn">
      <div className="flex items-start gap-2 text-amber-900 text-sm">
        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <span>{warn}</span>
      </div>
      <div className="mt-3 space-y-2 text-sm">
        {data?.existing && (
          <div className="rounded bg-white border border-amber-200 p-2">
            <div className="text-[11px] uppercase tracking-wider text-slate-500">Existing</div>
            <div className="text-slate-900">{data.existing}</div>
          </div>
        )}
        {data?.incoming && (
          <div className="rounded bg-white border border-amber-200 p-2">
            <div className="text-[11px] uppercase tracking-wider text-slate-500">Incoming</div>
            <div className="text-slate-900">{data.incoming}</div>
          </div>
        )}
      </div>
      {data?.options?.length > 0 && (
        <div className="mt-3">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Resolution options</div>
          <ul className="space-y-1 text-sm text-slate-800">
            {data.options.map((o, i) => (
              <li key={i} className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />{o}
              </li>
            ))}
          </ul>
        </div>
      )}
    </PanelShell>
  );
}

export function WarningPanel({ title, warn, data }) {
  return (
    <PanelShell testId="panel-warning" title={title || "Warning"} tone="warn">
      <div className="flex items-start gap-2 text-amber-900 text-sm">
        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <span>{warn}</span>
      </div>
      {data?.affected?.length > 0 && (
        <div className="mt-3 rounded bg-white border border-amber-200 p-2 text-sm">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Affected bookings</div>
          {data.affected.map((a, i) => (
            <div key={i} className="text-slate-900">{a.customer} · {a.when}</div>
          ))}
        </div>
      )}
      {data?.options?.length > 0 && (
        <div className="mt-3">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Options</div>
          <ul className="space-y-1 text-sm text-slate-800">
            {data.options.map((o, i) => (
              <li key={i} className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />{o}
              </li>
            ))}
          </ul>
        </div>
      )}
    </PanelShell>
  );
}

export function FollowUpPanel({ title, data }) {
  return (
    <PanelShell testId="panel-follow-up" title={title || "Follow-up task"}>
      <KV rows={[
        ["Task",    data?.task],
        ["Due",     data?.due],
        ["Channel", data?.channel],
      ]} />
    </PanelShell>
  );
}

export function IncidentPanel({ title, data }) {
  return (
    <PanelShell testId="panel-incident" title={title || "Incident"} tone="warn">
      <div className="text-sm font-semibold text-slate-900">{data?.title}</div>
      <div className="text-xs text-slate-600 mt-0.5">Reported by {data?.by} · Severity: {data?.severity}</div>
      {data?.actions?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {data.actions.map((a, i) => (
            <span key={i} className="text-[11px] rounded-full bg-white border border-amber-300 text-amber-900 px-2 py-0.5">{a}</span>
          ))}
        </div>
      )}
    </PanelShell>
  );
}

export function ActivityPanel({ title, data }) {
  return (
    <PanelShell testId="panel-activity" title={title || "Activity history"}>
      <ul className="space-y-2 text-sm">
        {(data?.events || []).map((e, i) => (
          <li key={i} className="flex items-start gap-2">
            <span className="mt-1 w-1.5 h-1.5 rounded-full bg-slate-400 flex-shrink-0" />
            <div>
              <div className="text-slate-900">{e.what}</div>
              <div className="text-[11px] text-slate-500">{e.at} · {e.by}</div>
            </div>
          </li>
        ))}
      </ul>
    </PanelShell>
  );
}

/**
 * Handover check: pickup OR return, same shape (odometer/fuel/condition/
 * keys), just different data and title. There was no existing panel for
 * this moment in the booking lifecycle (the catalog had chat→notepad→
 * draft→booking→confirmation and separately a pickup-reminder/prep-task
 * panel, but nothing for the actual handover check): added here using
 * the exact same KV/PanelShell building blocks every other panel uses,
 * not a new visual language.
 */
const FUEL_LABEL = { 0: "Empty", 25: "1/4", 50: "1/2", 75: "3/4", 100: "Full" };
const RATING_STYLE = {
  ok: "bg-emerald-100 text-emerald-800",
  minor_issue: "bg-amber-100 text-amber-800",
  damage: "bg-rose-100 text-rose-800",
  missing: "bg-rose-100 text-rose-800",
  na: "bg-slate-100 text-slate-500",
};
const RATING_LABEL = { ok: "OK", minor_issue: "Minor", damage: "Damage", missing: "Missing", na: "N/A" };

/**
 * Mirrors the real product's two handover surfaces: the Pickup Handover
 * summary card (components/pickup-handover/PickupHandoverPanel.jsx: title +
 * status badge + icon rows for Odometer/Fuel, same fuel 0/25/50/75/100 →
 * Empty/1/4/1/2/3/4/Full mapping) and the return Inspection Sheet's rated
 * condition checklist (components/InspectionSheet.jsx: OK/Minor/Damage/
 * Missing/N/A per item, plus Cleaning condition), not invented generic
 * "odometer/fuel/condition/keys" fields.
 */
export function HandoverPanel({ title, tone = "default", type = "pickup", data = {} }) {
  const heading = title || (type === "pickup" ? "Pickup Handover" : "Return Inspection");
  return (
    <PanelShell testId="panel-handover" title={heading} tone={tone}>
      <div className="flex items-center justify-end -mt-1 mb-2">
        <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 inline-flex items-center gap-1">
          <CheckCircle2 className="w-3 h-3" /> Completed
        </span>
      </div>
      <dl className="text-sm space-y-1.5">
        {data.odometer_km != null && (
          <HandoverRow icon={<Gauge className="w-3.5 h-3.5" />} label="Odometer" value={`${data.odometer_km.toLocaleString()} km`} />
        )}
        {data.fuel_level != null && (
          <HandoverRow icon={<Fuel className="w-3.5 h-3.5" />} label="Fuel" value={FUEL_LABEL[data.fuel_level] ?? `${data.fuel_level}%`} />
        )}
        {type === "pickup" && (
          <HandoverRow label="Existing damage" value={data.existing_damage_reviewed ? "Reviewed before release" : "Not reviewed"} />
        )}
        {type === "return" && data.cleaning_condition && (
          <HandoverRow label="Cleaning condition" value={data.cleaning_condition} />
        )}
      </dl>
      {data.checklist?.length > 0 && (
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Condition checklist</div>
          <div className="space-y-1">
            {data.checklist.map((row) => (
              <div key={row.label} className="flex items-center justify-between text-xs bg-slate-50 rounded-md px-2 py-1">
                <span className="text-slate-700">{row.label}</span>
                <span className={`px-1.5 py-0.5 rounded font-semibold ${RATING_STYLE[row.rating] || RATING_STYLE.na}`}>
                  {RATING_LABEL[row.rating] || "N/A"}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      {data.keys_items?.length > 0 && (
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Keys &amp; items</div>
          <div className="space-y-1">
            {data.keys_items.map((k) => (
              <div key={k.item} className="flex items-center gap-2 text-xs text-slate-700">
                <span className={`w-3.5 h-3.5 rounded-sm border flex items-center justify-center flex-none ${k.released ? "bg-[#1B4332] border-[#1B4332]" : "border-slate-300"}`}>
                  {k.released && <Check className="w-2.5 h-2.5 text-white" />}
                </span>
                {k.item}
              </div>
            ))}
          </div>
        </div>
      )}
      {data.note && (
        <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 p-2 text-xs text-slate-700">
          {data.note}
        </div>
      )}
    </PanelShell>
  );
}

function HandoverRow({ icon, label, value }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-slate-500 inline-flex items-center gap-1">{icon}{label}</dt>
      <dd className="font-medium text-slate-900 text-right">{value}</dd>
    </div>
  );
}

export function ChatFocusPanel({ title }) {
  return (
    <PanelShell testId="panel-chat-focus" title={title || "Live customer message"}>
      <div className="flex items-start gap-2 text-slate-700 text-sm">
        <Info className="w-4 h-4 mt-0.5 text-sky-600" />
        <span>See the customer&apos;s message on the left.  Advance when you&apos;re ready.</span>
      </div>
    </PanelShell>
  );
}
